import { createControlId } from '@avantia/questionset-model';

import { SvgIcon } from '@avantia/style-guide';
import { triggerModalActionEvent } from '../../../actions/eventActions';
import { HpClientQuestion } from '../../../types';
import { HpBasicButtonControlProps, HpOnClickEvent } from '../../../types/inputControlTypes';
import { showConfirmModal } from '../../widgets';
import { HtmlWidget } from '../../widgets/htmlWidget';
import { createMouseEvent } from '../inputControl';

export function BasicButtonControl({
  buttonText,
  buttonTextModifier,
  dependentActions,
  disabled,
  id,
  name,
  onClick,
  question,
  textAndStyle
}: HpBasicButtonControlProps): JSX.Element {
  const { buttonStyle, icon, iconSize, style, type } = textAndStyle;

  // TODO - For now, buttonText will decide whether or not to show the modal.
  const showModal: boolean = buttonText === 'Remove' || buttonText === 'Delete';
  const buttonId = showModal ? `${id}-modal` : id;
  const classNames: string[] = ['btn', `btn-${buttonStyle || 'primary'}`, 'animated'];
  if (dependentActions) {
    dependentActions.forEach(({ action }) => {
      classNames.push(`hp-${action}`);
    });
  }

  const onClickHandler = createMouseOnClickEventHandler(showModal, onClick, question, id);
  let displayText: string | JSX.Element = buttonText || '<No Text>';
  if (!buttonTextModifier) {
    displayText = <HtmlWidget html={displayText} />;
    if (icon) {
      classNames.push('hp-button-icon');
      displayText = (
        <>
          <SvgIcon type={icon as any} size={(iconSize as any) || 'tiny'} />
          {displayText}
        </>
      );
    }
  } else {
    displayText = buttonTextModifier(displayText);
  }

  return (
    <button
      id={createControlId({ questionId: buttonId })}
      name={name || id}
      onClick={onClickHandler}
      disabled={disabled === true}
      type={type || 'button'}
      style={style}
      className={classNames.sort().join(' ')}
    >
      {displayText}
    </button>
  );
}

function createMouseOnClickEventHandler(
  showModal: boolean,
  onClick: HpOnClickEvent,
  question: HpClientQuestion,
  id: string
): (event: React.MouseEvent<HTMLElement, MouseEvent>) => void {
  if (showModal) {
    return createShowModalEvent(question, onClick, id);
  } else {
    return createMouseEvent(onClick, { type: 'button-action', question });
  }
}

function createShowModalEvent(
  question: HpClientQuestion,
  onClick: HpOnClickEvent,
  id: string
): (event: React.MouseEvent<HTMLElement, MouseEvent>) => void {
  const { data } = question;
  const { textAndStyle } = data;
  const { subtext } = textAndStyle.standard;
  return (event) => {
    event.stopPropagation();
    triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'show' });
    return showConfirmModal({
      onConfirmClick: onClick,
      question,
      id,
      modalCopyText: {
        body: subtext || `Are you sure you want to remove this ${question.label || 'item'} from your quote?`,
        title: 'Confirm remove',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Remove',
        confirmButtonStyle: 'danger'
      }
    });
  };
}
